import { ScrollArea, Stack, Text, Title, useMantineTheme } from '@mantine/core'
import { signMessage } from '@wagmi/core'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { keccak256, parseUnits, toBytes } from 'viem'

import RootButton from '@/components/Buttons/RootButton'
import { Amount } from '@/components/CurrencyInput'
import UserDetails from '@/components/Details/UserDetails/UserDetails'
import classes from '@/components/Discover/Feed/MembersList.module.css'
import ErrorMessage from '@/components/ErrorMessage'
import { ConfirmationLoading, ConfirmationReady } from '@/components/Identity/ConfirmModals/utils'
import { STUDIO_BOT } from '@/constants/blockchain'
import { useElectionsContext } from '@/contexts/ElectionsContext'
import { CastVotesPayload, CastVotesResponse } from '@/hooks/useGovernance'
import useSpacesAuth from '@/hooks/useSpacesAuth'
import { useAuth } from '@/plugins/auth'
import { getWagmiConfig } from '@/plugins/auth/config'
import { post } from '@/utils/utils'

type Vote = {
  candidate: string
  amount: string
}

const splitVotes = (vtes: { [key: string]: Amount }): Vote[] => {
  // integer bigger than 1
  const minSplits = 2
  // integer lower than 100 divided by increment
  const maxSplits = 5

  const splitVotesArray: Vote[] = []

  Object.entries(vtes).forEach(([candidate, v]) => {
    const totalAmount = Number(v.value)
    const splits = Math.floor(Math.random() * (maxSplits - minSplits + 1)) + minSplits

    // Generate random split percentages that sum to 100%
    const splitPercentages = Array.from({ length: splits }, () => Math.random())
    const sumOfSplitPercentages = splitPercentages.reduce((acc, curr) => acc + curr, 0)
    const normalizedSplitPercentages = splitPercentages.map(
      percentage => percentage / sumOfSplitPercentages
    )

    let remainingAmount = parseUnits(totalAmount.toString(), 18)
    for (let i = 0; i < splits - 1; i++) {
      const splitAmount = totalAmount * normalizedSplitPercentages[i]
      const splitAmountBigInt = parseUnits(splitAmount.toString(), 18)
      splitVotesArray.push({ candidate, amount: `0x${splitAmountBigInt.toString(16)}` })
      remainingAmount -= splitAmountBigInt
    }

    splitVotesArray.push({ candidate, amount: `0x${remainingAmount.toString(16)}` })
  })

  return splitVotesArray
}

const hashVotes = (votesArray: Vote[]): string => {
  const bytesArray = votesArray
    .flatMap(({ candidate, amount }) => [toBytes(candidate), toBytes(amount)])
    .reduce((acc, val) => {
      const tempArray = new Uint8Array(acc.length + val.length)
      tempArray.set(acc)
      tempArray.set(val, acc.length)
      return tempArray
    }, new Uint8Array())

  return keccak256(bytesArray)
}

interface CastVotesModalProps {
  isMobile: boolean | undefined
  votes: { [key: string]: Amount }
  setVotes: React.Dispatch<
    React.SetStateAction<{
      [key: string]: Amount
    }>
  >
  close: () => void
}

const CastVotesModal: React.FC<CastVotesModalProps> = ({ isMobile, votes, setVotes, close }) => {
  const { address } = useAuth()
  const theme = useMantineTheme()
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const { refetchData } = useElectionsContext()

  const handleClose = useCallback(() => {
    close()
    refetchData()
  }, [close, refetchData])
  // Hooks
  const { token } = useSpacesAuth()

  const voteAction = useCallback(async () => {
    try {
      if (!token) return
      setIsLoading(true)
      const splitVotesArray = splitVotes(votes)
      console.log('splitVotesArray', splitVotesArray)
      const votesHash = hashVotes(splitVotesArray)
      console.log('votesHash', votesHash)
      // Prompt user to sign the hash of the votes
      const signature = await signMessage(getWagmiConfig(), {
        message: `I want to cast votes with the following hash: ${votesHash}`,
      })
      console.log('signature', signature)
      if (signature) {
        const payload: CastVotesPayload = {
          votes: splitVotesArray,
          signature,
        }

        const retval = (await post(
          `${STUDIO_BOT}/elections/vote`,
          JSON.stringify(payload),
          token
        )) as unknown as CastVotesResponse

        if (retval.valid) {
          setVotes({})
          setSuccess(true)
          setError('')
        } else {
          // handle error
          setError(retval?.error || 'Error casting votes')
        }
      }
    } catch (err: any) {
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }, [token, votes, signMessage, splitVotes, hashVotes])
  return (
    <>
      {!success && !error && !isLoading && (
        <Stack>
          <Text>
            Are you sure you want to cast these VOTE tokens for these candidates? This action cannot
            be undone for this election.
          </Text>
          {/* go through votes amounts */}
          <ScrollArea h={400}>
            <Stack gap="10px">
              {Object.entries(votes)
                .filter(([, v]) => Number(v.value) > 0)
                .sort(([, a], [, b]) => Number(b.value) - Number(a.value))
                .map(([nominee, v]) => (
                  <Stack
                    key={nominee}
                    gap="10px"
                    style={{ flexDirection: isMobile ? 'column' : 'row', width: '100%' }}
                  >
                    <Stack
                      key={nominee}
                      className={classes.memberNoHover}
                      style={{
                        width: '100%',
                        justifyContent: 'space-between',
                        flexDirection: 'row',
                        borderColor: address === nominee ? theme.colors.l1Primary[0] : '',
                      }}
                    >
                      <UserDetails userId={nominee} />
                    </Stack>
                    <Stack
                      gap={0}
                      className={classes.memberNoHover}
                      style={{
                        gap: isMobile ? '5px' : '0px',
                        flexDirection: isMobile ? 'row' : 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: isMobile ? '100%' : '120px',
                      }}
                    >
                      <Title order={5}> {v.value}</Title>
                      <Text>Votes</Text>
                    </Stack>
                  </Stack>
                ))}
            </Stack>
          </ScrollArea>
          <Stack style={{ flexDirection: 'row', width: '100%' }}>
            <RootButton style1={false} secondary expand onClick={handleClose}>
              Close
            </RootButton>
            <RootButton style1={false} expand onClick={voteAction}>
              Confirm
            </RootButton>
          </Stack>
        </Stack>
      )}
      {success && !error && !isLoading && (
        <ConfirmationReady
          success={handleClose}
          message={t(
            'components.governance.castVotesModal.successMessage',
            'Votes cast successfully'
          )}
        />
      )}
      {!error && isLoading && (
        <ConfirmationLoading
          title={t('components.governance.castVotesModal.loading', 'Casting Votes...')}
        />
      )}
      {error && !isLoading && (
        <ErrorMessage
          title={t('components.governance.castVotesModal.error', 'Error Casting Votes')}
          message={error}
        />
      )}
    </>
  )
}

export default CastVotesModal
